import React, { useEffect, useRef, useState } from "react";
import { productPhotos } from "../data/productPhotos";

export default function ProductCarousel() {
  const track = useRef(null);
  const current = useRef(0);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(true);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(track.current);
    return () => { media.removeEventListener("change", update); observer.disconnect(); };
  }, []);

  const move = (step) => {
    const node = track.current;
    if (!node) return;
    const next = (current.current + step + productPhotos.length) % productPhotos.length;
    node.scrollTo({ left: next * node.clientWidth, behavior: reduced ? "auto" : "smooth" });
  };

  useEffect(() => {
    if (paused || interacting || !visible || reduced) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) move(1);
    }, 4500);
    return () => window.clearInterval(timer);
    // move reads the current slide from a ref; autoplay restarts after interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, interacting, visible, reduced]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const node = track.current;
      node?.scrollTo({ left: current.current * node.clientWidth, behavior: "instant" });
    });
    observer.observe(track.current);
    return () => observer.disconnect();
  }, []);

  return <div className="product-carousel" role="region" aria-roledescription="カルーセル" aria-label="取扱商品の写真"
    onMouseEnter={() => setInteracting(true)} onMouseLeave={() => setInteracting(false)}
    onFocusCapture={() => setInteracting(true)}
    onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false); }}>
    <div ref={track} className="product-carousel__track" tabIndex={0} aria-label="左右にスワイプして商品写真を表示"
      onTouchStart={() => setPaused(true)}
      onKeyDown={(event) => { if (event.key === "ArrowRight" || event.key === "ArrowLeft") { event.preventDefault(); move(event.key === "ArrowRight" ? 1 : -1); } }}
      onScroll={() => {
        const next = Math.round(track.current.scrollLeft / track.current.clientWidth);
        current.current = next;
        setIndex(next);
      }}>
      {productPhotos.map((photo, i) => <div className="product-carousel__slide" key={photo.src} role="group" aria-roledescription="スライド" aria-label={`${i + 1} / ${productPhotos.length}`}>
        <img src={photo.src} alt={photo.alt} width="750" height="1100" loading="lazy" draggable="false" />
      </div>)}
    </div>
    <div className="product-carousel__controls">
      <span>{String(index + 1).padStart(2, "0")} <i>/ {String(productPhotos.length).padStart(2, "0")}</i></span>
      <div>
        {!reduced && <button type="button" onClick={() => setPaused(!paused)} aria-label={paused ? "自動再生を開始" : "自動再生を停止"}>{paused ? "再生" : "停止"}</button>}
        <button type="button" onClick={() => move(-1)} aria-label="前の商品写真">←</button>
        <button type="button" onClick={() => move(1)} aria-label="次の商品写真">→</button>
      </div>
    </div>
  </div>;
}
